import { useCallback } from "react";
import { match } from "ts-pattern";
import type { TimestampedTag, TimestampedTopicTag } from "~/domain/logs";
import {
  checkIsTimestampedLogTag,
  checkIsTimestampedTopicTag,
  useAllTags,
} from "~/domain/logs";
import type { TagListResponse } from "~/lqs";
import { usePlayerParams } from "../hooks";
import { useVisibleTagsSetting } from "./store";

export type PlaybackTag = TimestampedTag;

export function usePlaybackTags() {
  const { logId } = usePlayerParams();

  const visibleTagsSetting = useVisibleTagsSetting();

  const enabled = visibleTagsSetting !== null;

  const select = useCallback(
    (response: TagListResponse): ReadonlyArray<PlaybackTag> => {
      if (!enabled) {
        return [];
      }

      return match(visibleTagsSetting)
        .with({ type: "log" }, () =>
          response.data
            .filter(
              (tag) =>
                // Topic tags are intentionally included. The original idea was
                // for only log tags to be included but there was a bug where
                // topic tags were also included. That turned out to be a
                // behavior users liked so we're keeping it that way for now.
                checkIsTimestampedLogTag(tag) ||
                checkIsTimestampedTopicTag(tag),
            )
            .sort(compareTimestampedTags),
        )
        .with({ type: "topic" }, (visibleTagsSetting) =>
          response.data
            .filter(
              (tag): tag is TimestampedTopicTag =>
                checkIsTimestampedTopicTag(tag) &&
                tag.topicId === visibleTagsSetting.topicId,
            )
            .sort(compareTimestampedTags),
        )
        .exhaustive();
    },
    [enabled, visibleTagsSetting],
  );

  return useAllTags(logId, { enabled, select });
}

function compareTimestampedTags(a: TimestampedTag, b: TimestampedTag): number {
  if (a.startTime < b.startTime) {
    return -1;
  } else if (a.startTime > b.startTime) {
    return 1;
  } else {
    return 0;
  }
}
